import React, { useEffect, useRef, useState } from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import FarmFeedRowComment from './FarmFeedRowComment';
// utils
import cn from 'classnames';
import { getMentionedCommentId } from 'utils/activityHelper';
// styles
import './FarmFeedRowComments.scss';

const VISIBLE_COMMENTS_COUNT = 3;

const FarmFeedRowComments = ({ activity }, { router }) => {
  const { comments, company } = activity;
  // A mention link (?mentionCommentId=) opens the event with that comment visible and highlighted
  const mentionCommentId = getMentionedCommentId(activity, router?.location);
  const mentionIndex = comments.findIndex(({ id }) => id === mentionCommentId);
  const [isCollapsed, setIsCollapsed] = useState(mentionIndex < VISIBLE_COMMENTS_COUNT);
  const mentionedCommentRef = useRef(null);
  const shownMentionIdRef = useRef(null);
  const isScrollPendingRef = useRef(false);
  const showCollapser = comments.length > VISIBLE_COMMENTS_COUNT;
  const commentsList = isCollapsed ? comments.slice(0, VISIBLE_COMMENTS_COUNT) : comments;

  useEffect(() => {
    // Once per mention: new or deleted comments must not expand the list and scroll the page again
    if (!mentionCommentId || shownMentionIdRef.current === mentionCommentId) return;
    shownMentionIdRef.current = mentionCommentId;
    isScrollPendingRef.current = true;
    if (mentionIndex >= VISIBLE_COMMENTS_COUNT) setIsCollapsed(false);
  }, [mentionCommentId]);

  useEffect(() => {
    // Scroll on the first commit that has the comment rendered (the list may still be collapsed on this one)
    if (!isScrollPendingRef.current || !mentionedCommentRef.current) return;
    isScrollPendingRef.current = false;
    mentionedCommentRef.current.scrollIntoView({ block: 'center' });
  });

  const toggleComment = (e) => {
    e.preventDefault();
    setIsCollapsed(!isCollapsed);
  };

  return !!comments.length && (
    <div className="FarmFeedRowComments">
      {commentsList.map((comment) => {
        const isMention = comment.id === mentionCommentId;
        return (
          <div
            key={comment.id}
            ref={isMention ? mentionedCommentRef : undefined}
            className={cn({ 'mentioned-comment': isMention })}
          >
            <FarmFeedRowComment
              comment={comment}
              activity={activity}
              company={company}
            />
          </div>
        );
      })}
      {showCollapser && (
        <div className="show-more-comments">
          <span className="comments-collapser" onClick={toggleComment}>
            <FormattedMessage id={`component.comments.${isCollapsed ? 'show' : 'hide'}Comments`} />
          </span>
          <FormattedMessage
            id="component.pagination.pageOfTotal"
            values={{ page: commentsList.length, total: comments.length }}
          />
        </div>
      )}
    </div>
  );
};

FarmFeedRowComments.contextTypes = {
  router: T.object,
};

FarmFeedRowComments.propTypes = {
  activity: T.object.isRequired,
};

export default FarmFeedRowComments;
